import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import AnimatedSection from "../../components/common/AnimatedSection";
import SectionHeading from "../../components/common/SectionHeading";
import Button from "../../components/common/Button";
import ContactForm from "../../components/forms/ContactForm";
import { useCarousel } from "../../hooks/useCarousel";
import kidsRoomImage from "../../assets/images/room/KIDS-ROOM.jpg";
import doorsImage from "../../assets/images/room/DOORS.jpeg";

// Room data
const roomData = {
  kitchen: {
    name: "Kitchen",
    tagline: "The heart of your home, precision-engineered.",
    description:
      "Modular kitchens designed for Indian cooking styles, built with BWP Ply for wet areas and premium hardware that lasts decades.",
    heroImage:
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1920&q=80",
    layouts: [
      {
        id: "straight",
        name: "Straight",
        description: "Ideal for small spaces and galley kitchens.",
      },
      {
        id: "l-shaped",
        name: "L-Shaped",
        description: "Most popular layout for Indian homes.",
      },
      {
        id: "u-shaped",
        name: "U-Shaped",
        description: "Maximum storage and counter space.",
      },
      {
        id: "parallel",
        name: "Parallel",
        description: "Efficient workflow for serious cooks.",
      },
      {
        id: "island",
        name: "Island",
        description: "Premium option for open layouts.",
      },
    ],
    features: [
      "BWP Ply construction for under-sink and wet areas",
      "Soft-close hinges and drawer channels",
      "Chimney integration and electrical planning",
      "Stone, quartz, or granite countertops",
      "Modular accessories – corner units, pullouts, organizers",
      "Custom sizing to maximize space utilization",
    ],
    materials: [
      { name: "BWP Ply", use: "Under-sink cabinets, wet zones" },
      { name: "BWR Ply", use: "Base cabinets, splash areas" },
      { name: "MR Ply", use: "Wall cabinets, dry storage" },
      { name: "HDHMR", use: "Drawer boxes, internal components" },
    ],
    gallery: [
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&q=80",
      "https://images.unsplash.com/photo-1600585152220-90363fe7e115?w=600&q=80",
      "https://images.unsplash.com/photo-1556909172-54557c7e4fb7?w=600&q=80",
      "https://images.unsplash.com/photo-1600489000022-c2086d79f9d4?w=600&q=80",
    ],
  },
  living: {
    name: "Living Room",
    tagline: "Where families connect, designed for life.",
    description:
      "Living room solutions that combine style with function – TV units, display shelves, and storage that makes your space work harder.",
    heroImage:
      "https://images.unsplash.com/photo-1618219908412-a29a1bb7b86e?w=1920&q=80",
    layouts: [
      {
        id: "minimal",
        name: "Minimal",
        description: "Clean lines, essential pieces only.",
      },
      {
        id: "storage-rich",
        name: "Storage Rich",
        description: "Maximum storage integrated beautifully.",
      },
      {
        id: "entertainment",
        name: "Entertainment",
        description: "Centered around TV and audio systems.",
      },
    ],
    features: [
      "Custom TV units with cable management",
      "Display shelves and bookcases",
      "Hidden storage solutions",
      "Feature wall paneling",
      "Console units and sideboards",
      "Integrated lighting options",
    ],
    materials: [
      { name: "MR Ply", use: "TV units, shelving, cabinets" },
      { name: "HDHMR", use: "Panel backs, drawer components" },
    ],
    gallery: [
      "https://images.unsplash.com/photo-1618219908412-a29a1bb7b86e?w=600&q=80",
      "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=600&q=80",
      "https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?w=600&q=80",
    ],
  },
  bedroom: {
    name: "Bedroom",
    tagline: "Your personal sanctuary, perfectly planned.",
    description:
      "Bedroom interiors that maximize rest and storage – built-in wardrobes, study corners, and headboard units designed for your lifestyle.",
    heroImage:
      "https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=1920&q=80",
    layouts: [
      {
        id: "master",
        name: "Master Suite",
        description: "Walk-in wardrobe and dresser integration.",
      },
      {
        id: "guest",
        name: "Guest Room",
        description: "Essential storage, clean design.",
      },
      {
        id: "compact",
        name: "Compact",
        description: "Smart solutions for smaller rooms.",
      },
    ],
    features: [
      "Built-in wardrobes and storage",
      "Study corner integration",
      "Headboard units with storage",
      "Dresser and vanity units",
      "Bedside tables and lamps",
      "Ambient lighting design",
    ],
    materials: [
      { name: "MR Ply", use: "Wardrobes, bed backs, shelving" },
      { name: "HDHMR", use: "Wardrobe interiors, drawers" },
    ],
    gallery: [
      "https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=600&q=80",
      "https://images.unsplash.com/photo-1617325247661-675ab4b64ae2?w=600&q=80",
    ],
  },
  wardrobe: {
    name: "Wardrobe",
    tagline: "Organized spaces for everything you own.",
    description:
      "From sliding wardrobes to walk-in closets, we design storage that fits your collection and lifestyle perfectly.",
    heroImage:
      "https://images.unsplash.com/photo-1558997519-83ea9252edf8?w=1920&q=80",
    layouts: [
      {
        id: "hinged",
        name: "Hinged Doors",
        description: "Classic design, full access.",
      },
      {
        id: "sliding",
        name: "Sliding Doors",
        description: "Space-saving, modern look.",
      },
      {
        id: "walk-in",
        name: "Walk-In",
        description: "Ultimate storage experience.",
      },
    ],
    features: [
      "Custom internal configurations",
      "Saree and dhoti organizers",
      "Shoe racks and accessory trays",
      "Soft-close mechanisms throughout",
      "Built-in lighting options",
      "Full-length mirror integration",
    ],
    materials: [
      { name: "MR Ply", use: "Main structure and shelving" },
      { name: "HDHMR", use: "Internal components, drawer boxes" },
    ],
    gallery: [
      "https://images.unsplash.com/photo-1558997519-83ea9252edf8?w=600&q=80",
      "https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?w=600&q=80",
    ],
  },
  dining: {
    name: "Dining",
    tagline: "Where meals become memories.",
    description:
      "Dining spaces that encourage gathering – crockery units, bar counters, and feature walls that make every meal special.",
    heroImage:
      "https://images.unsplash.com/photo-1617806118233-18e1de247200?w=1920&q=80",
    layouts: [
      {
        id: "formal",
        name: "Formal",
        description: "Traditional dining setup.",
      },
      {
        id: "open",
        name: "Open Kitchen",
        description: "Integrated with kitchen island.",
      },
      {
        id: "breakfast",
        name: "Breakfast Nook",
        description: "Cozy corner dining.",
      },
    ],
    features: [
      "Crockery and display units",
      "Bar counters and wine storage",
      "Feature wall paneling",
      "Buffet and sideboard units",
      "Integrated lighting",
      "Custom dining benches",
    ],
    materials: [
      { name: "MR Ply", use: "Display units, sideboards" },
      { name: "BWR Ply", use: "Bar counters near wet areas" },
    ],
    gallery: [
      "https://images.unsplash.com/photo-1617806118233-18e1de247200?w=600&q=80",
      "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=600&q=80",
    ],
  },
  kids: {
    name: "Kids Room",
    tagline: "Fun spaces that grow with them.",
    description:
      "Kids room designs that balance play, study, and rest – with safe materials and smart storage that adapts as they grow.",
    heroImage:
      "https://images.unsplash.com/photo-1618221118493-9cfa1a1c00da?w=1920&q=80",
    layouts: [
      {
        id: "single",
        name: "Single Child",
        description: "Study, storage, and play zones.",
      },
      {
        id: "shared",
        name: "Shared Room",
        description: "Bunk beds and dual study areas.",
      },
      {
        id: "teen",
        name: "Teen Room",
        description: "More mature design, tech integration.",
      },
    ],
    features: [
      "Bunk beds and loft beds",
      "Study desks with storage",
      "Toy storage and display",
      "Safe, rounded edges",
      "Colorful laminate options",
      "Growth-adaptable designs",
    ],
    materials: [
      { name: "MR Ply", use: "All furniture, safe and stable" },
      { name: "HDHMR", use: "Drawer components" },
    ],
    gallery: [
      kidsRoomImage,
      "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=600&q=80",
    ],
  },
  study: {
    name: "Study",
    tagline: "Workspaces that inspire productivity.",
    description:
      "Home offices and study rooms designed for focus – ergonomic desks, book storage, and cable management built in.",
    heroImage:
      "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1920&q=80",
    layouts: [
      {
        id: "home-office",
        name: "Home Office",
        description: "Full setup for WFH professionals.",
      },
      {
        id: "study-corner",
        name: "Study Corner",
        description: "Compact solution for any room.",
      },
      { id: "library", name: "Library", description: "Book lovers' paradise." },
    ],
    features: [
      "Ergonomic desk configurations",
      "Book shelving and display",
      "Cable management solutions",
      "Monitor and equipment mounts",
      "Task lighting integration",
      "Acoustic considerations",
    ],
    materials: [
      { name: "MR Ply", use: "Desks, shelving, cabinets" },
      { name: "HDHMR", use: "Desk tops, drawer units" },
    ],
    gallery: [
      "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=600&q=80",
      "https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?w=600&q=80",
    ],
  },
  pooja: {
    name: "Pooja Room",
    tagline: "Sacred spaces, crafted with reverence.",
    description:
      "Pooja rooms and mandirs that honor tradition while fitting modern homes – from compact units to dedicated rooms.",
    heroImage:
      "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=1920&q=80",
    layouts: [
      {
        id: "wall-unit",
        name: "Wall Unit",
        description: "Compact pooja unit for any room.",
      },
      {
        id: "dedicated",
        name: "Dedicated Room",
        description: "Full pooja room design.",
      },
      {
        id: "corner",
        name: "Corner Mandir",
        description: "Elegant corner solution.",
      },
    ],
    features: [
      "Traditional jaali and carving options",
      "Brass and copper accents",
      "Storage for pooja items",
      "Concealed lighting",
      "Bell and lamp hooks",
      "Easy-clean surfaces",
    ],
    materials: [
      { name: "MR Ply", use: "Main structure, shelving" },
      { name: "Solid Wood", use: "Decorative elements (optional)" },
    ],
    gallery: [
      "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=600&q=80",
    ],
  },
  "space-saving": {
    name: "Space Saving Furniture",
    tagline: "Smart solutions for compact living.",
    description:
      "We understand small spaces need big thinking. Our space-saving furniture maximizes every square foot without compromising on style or comfort.",
    heroImage:
      "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1920&q=80",
    layouts: [
      {
        id: "multi-functional",
        name: "Multi-Functional",
        description: "Furniture that serves multiple purposes.",
      },
      {
        id: "hidden-storage",
        name: "Hidden Storage",
        description: "Storage solutions that blend seamlessly.",
      },
      {
        id: "compact-designs",
        name: "Compact Designs",
        description: "Space-efficient furniture for small homes.",
      },
    ],
    features: [
      "Sofa-cum-beds for living rooms",
      "Storage beds with drawers",
      "Foldable dining tables",
      "Wall-mounted desks and shelves",
      "Nesting tables and stackable furniture",
      "Corner units that maximize space",
    ],
    materials: [
      { name: "MR Ply", use: "Main structure, panels" },
      { name: "HDHMR", use: "Drawer boxes, internal components" },
      { name: "Metal", use: "Frames for foldable furniture" },
    ],
    gallery: [
      "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=600&q=80",
      "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=600&q=80",
      "https://images.unsplash.com/photo-1600573472550-8090b5e0745e?w=600&q=80",
    ],
  },
  "false-ceiling": {
    name: "False Ceiling",
    tagline: "Elevate your space from above.",
    description:
      "Beautiful false ceilings add depth, character, and sophistication to your spaces. We design them with concealed lighting and modern patterns that transform your entire home.",
    heroImage:
      "https://images.unsplash.com/photo-1600121848594-d8644e57abab?w=1920&q=80",
    layouts: [
      {
        id: "modern",
        name: "Modern",
        description: "Clean lines and contemporary designs.",
      },
      {
        id: "traditional",
        name: "Traditional",
        description: "Classic patterns and elegant details.",
      },
      {
        id: "minimal",
        name: "Minimal",
        description: "Simple designs with hidden lighting.",
      },
    ],
    features: [
      "Concealed LED lighting integration",
      "Modern geometric patterns",
      "Gypsum and POP finishes",
      "Cove lighting designs",
      "Feature ceiling panels",
      "Acoustic solutions for noise control",
    ],
    materials: [
      { name: "Gypsum Board", use: "Main ceiling panels" },
      { name: "POP", use: "Decorative elements and molding" },
      { name: "Metal Framework", use: "Support structure" },
    ],
    gallery: [
      "/images/false-ceiling/gallery-1.jpg",
      "/images/false-ceiling/gallery-2.jpg",
      "/images/false-ceiling/gallery-3.jpg",
      "/images/false-ceiling/gallery-4.jpg",
      "/images/false-ceiling/gallery-5.jpg",
      "/images/false-ceiling/gallery-6.jpg",
      "/images/false-ceiling/gallery-7.jpg",
      "/images/false-ceiling/gallery-8.jpg",
      "/images/false-ceiling/gallery-9.jpg",
      "/images/false-ceiling/gallery-10.jpg",
      "/images/false-ceiling/gallery-11.jpg",
      "/images/false-ceiling/gallery-12.jpg",
    ],
  },
  doors: {
    name: "Doors",
    tagline: "First impressions that last.",
    description:
      "Doors that welcome you home and complement your interior style. From sleek modern designs to classic elegance, we craft doors with precision and premium materials.",
    heroImage:
      "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?w=1920&q=80",
    layouts: [
      {
        id: "modern",
        name: "Modern",
        description: "Sleek designs with clean lines.",
      },
      {
        id: "classic",
        name: "Classic",
        description: "Traditional panel doors with elegance.",
      },
      {
        id: "sliding",
        name: "Sliding",
        description: "Space-saving sliding door solutions.",
      },
    ],
    features: [
      "Custom-designed to match your style",
      "Premium hardware and hinges",
      "Sound-proofing options",
      "Fire-resistant doors available",
      "Flush doors and panel doors",
      "Sliding and folding door options",
    ],
    materials: [
      { name: "MR Ply", use: "Door panels and frames" },
      { name: "Solid Wood", use: "Premium door options" },
      { name: "HDHMR", use: "Internal core for durability" },
    ],
    gallery: [
      doorsImage,
      "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=600&q=80",
      "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=600&q=80",
    ],
  },
  lighting: {
    name: "Lighting",
    tagline: "The right light transforms every space.",
    description:
      "Lighting that enhances ambiance and functionality. From task lighting to ambient mood lighting, we create the perfect atmosphere for every space in your home.",
    heroImage:
      "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1920&q=80",
    layouts: [
      {
        id: "ambient",
        name: "Ambient Lighting",
        description: "Soft, warm lighting for relaxation.",
      },
      {
        id: "task",
        name: "Task Lighting",
        description: "Focused light for work and activities.",
      },
      {
        id: "accent",
        name: "Accent Lighting",
        description: "Highlight features and create drama.",
      },
    ],
    features: [
      "Concealed LED strip lighting",
      "Recessed downlights and spotlights",
      "Pendant lights and chandeliers",
      "Wall sconces and decorative fixtures",
      "Smart lighting controls and dimmers",
      "Cove lighting and indirect lighting",
    ],
    materials: [
      { name: "LED Strips", use: "Concealed and accent lighting" },
      { name: "Downlights", use: "General and task lighting" },
      { name: "Decorative Fixtures", use: "Feature and ambient lighting" },
    ],
    gallery: [
      "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=600&q=80",
      "https://images.unsplash.com/photo-1616137466211-f939a420be84?w=600&q=80",
      "https://images.unsplash.com/photo-1600573472550-8090b5e0745e?w=600&q=80",
    ],
  },
};

export default function RoomPage() {
  const { roomType } = useParams();
  const room = roomData[roomType] || roomData.kitchen;
  const [activeLayout, setActiveLayout] = React.useState(room.layouts[0].id);
  const pinterestRef = React.useRef(null);

  const { emblaRef, scrollPrev, scrollNext } = useCarousel({ loop: true });

  // Load Pinterest script for false-ceiling page
  useEffect(() => {
    if (roomType === "false-ceiling") {
      // Check if script already exists
      const existingScript = document.querySelector(
        'script[src*="pinterest.com/js/pinit.js"]'
      );

      if (!existingScript) {
        const script = document.createElement("script");
        script.src = "https://assets.pinterest.com/js/pinit.js";
        script.async = true;
        script.defer = true;
        script.onload = () => {
          // Trigger Pinterest to build embeds after script loads
          if (window.PinUtils) {
            window.PinUtils.build();
          }
        };
        document.body.appendChild(script);
      } else {
        // If script exists, trigger Pinterest to re-render
        setTimeout(() => {
          if (window.PinUtils) {
            window.PinUtils.build();
          }
        }, 100);
      }
    }
  }, [roomType]);

  return (
    <div>
      {/* Hero */}
      <section className="relative py-32 lg:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={room.heroImage}
            alt={room.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-primary/95 via-brand-primary/70 to-brand-primary/40" />
        </div>
        <div className="container-custom section-padding relative">
          <AnimatedSection className="max-w-3xl text-white">
            <span className="text-brand-accent text-sm font-medium uppercase tracking-wider">
              Design by Room
            </span>
            <h1 className="font-heading text-display-1 mt-4 mb-4 text-white">
              {room.name}
            </h1>
            <p className="text-2xl text-white/90 mb-4">{room.tagline}</p>
            <p className="text-lg text-white/70 leading-relaxed">
              {room.description}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Layouts */}
      <section className="section-spacing bg-white">
        <div className="container-custom section-padding">
          <SectionHeading
            label="Choose Your Layout"
            title={`${room.name} layout options.`}
            align="center"
          />

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {room.layouts.map((layout) => (
              <button
                key={layout.id}
                onClick={() => setActiveLayout(layout.id)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all ${
                  activeLayout === layout.id
                    ? "bg-brand-accent text-white shadow-md"
                    : "bg-background-card text-text-secondary hover:text-brand-primary"
                }`}
              >
                {layout.name}
              </button>
            ))}
          </div>

          <AnimatedSection key={activeLayout} animation="fade">
            <div className="bg-background-card rounded-3xl p-8 lg:p-12 text-center">
              <h3 className="font-heading text-2xl text-brand-primary mb-4">
                {room.layouts.find((l) => l.id === activeLayout)?.name}{" "}
                {room.name}
              </h3>
              <p className="text-text-secondary max-w-2xl mx-auto mb-8">
                {room.layouts.find((l) => l.id === activeLayout)?.description}
              </p>

              {/* Pinterest Board for False Ceiling - All Layouts */}
              {roomType === "false-ceiling" ? (
                <div className="mt-8">
                  <div
                    className="pinterest-board-container"
                    dangerouslySetInnerHTML={{
                      __html: `
                        <a 
                          data-pin-do="embedBoard" 
                          data-pin-board-width="900" 
                          data-pin-scale-height="400" 
                          data-pin-scale-width="115" 
                          href="https://in.pinterest.com/iravathamh/false-ceiling/">
                        </a>
                      `,
                    }}
                  />
                </div>
              ) : room.layouts.find((l) => l.id === activeLayout)?.images ? (
                /* Layout Images for other layouts */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                  {room.layouts
                    .find((l) => l.id === activeLayout)
                    ?.images.map((img, index) => (
                      <div
                        key={index}
                        className="aspect-[4/3] rounded-2xl overflow-hidden"
                      >
                        <img
                          src={img}
                          alt={`${
                            room.layouts.find((l) => l.id === activeLayout)
                              ?.name
                          } design ${index + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                    ))}
                </div>
              ) : null}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Features */}
      <section className="section-spacing bg-background-main">
        <div className="container-custom section-padding">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <AnimatedSection>
              <SectionHeading
                label="What's Included"
                title={`${room.name} features.`}
                align="left"
                className="mb-8"
              />
              <ul className="space-y-4">
                {room.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-brand-accent/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-brand-accent" />
                    </div>
                    <span className="text-text-primary">{feature}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>

            <AnimatedSection animation="fade-left">
              {roomType === "false-ceiling" ? (
                <div className="rounded-3xl overflow-hidden bg-white p-8">
                  {/* Pinterest Board Embed */}
                  <div ref={pinterestRef}>
                    <a
                      data-pin-do="embedBoard"
                      data-pin-board-width="900"
                      data-pin-scale-height="400"
                      data-pin-scale-width="115"
                      href="https://in.pinterest.com/iravathamh/false-ceiling/"
                    >
                      View False Ceiling Designs on Pinterest
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  <div className="overflow-hidden rounded-3xl" ref={emblaRef}>
                    <div className="flex">
                      {room.gallery.map((img, i) => (
                        <div key={i} className="flex-none w-full">
                          <div className="aspect-[4/3]">
                            <img
                              src={img}
                              alt={`${room.name} ${i + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-center gap-3 mt-4">
                    <button
                      onClick={scrollPrev}
                      className="w-10 h-10 rounded-full border border-border-light flex items-center justify-center hover:bg-brand-primary hover:text-white hover:border-brand-primary transition-all"
                    >
                      <ArrowRight className="w-4 h-4 rotate-180" />
                    </button>
                    <button
                      onClick={scrollNext}
                      className="w-10 h-10 rounded-full border border-border-light flex items-center justify-center hover:bg-brand-primary hover:text-white hover:border-brand-primary transition-all"
                    >
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="section-spacing bg-white">
        <div className="container-custom section-padding">
          <SectionHeading
            label="Quality Materials"
            title={`What we use for ${room.name.toLowerCase()}s.`}
            align="center"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {room.materials.map((material, index) => (
              <AnimatedSection key={material.name} delay={index * 100}>
                <div className="bg-background-card rounded-2xl p-6 text-center">
                  <h4 className="font-heading text-lg text-brand-primary mb-2">
                    {material.name}
                  </h4>
                  <p className="text-text-secondary text-sm">{material.use}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection className="mt-12 text-center">
            <p className="text-text-secondary max-w-2xl mx-auto">
              We never use MDF. All our {room.name.toLowerCase()} solutions are
              built with MR, BWR, BWP Ply or HDHMR – chosen based on moisture
              exposure and usage patterns.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Lead Form */}
      <section
        className="section-spacing bg-background-main"
        id="budget-estimator"
      >
        <div className="container-custom section-padding">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <AnimatedSection>
              <SectionHeading
                label="Get Started"
                title={`Planning your ${room.name.toLowerCase()} in the next 90 days?`}
                align="left"
                className="mb-6"
              />
              <p className="text-text-secondary leading-relaxed mb-8">
                Book a consultation with our {room.name.toLowerCase()} design
                experts. We'll understand your needs, show you possibilities,
                and provide a detailed estimate – all with no obligation.
              </p>
              <div className="space-y-4">
                {[
                  "Design consultation",
                  "Detailed cost estimate",
                  "3D visualizations",
                  "No obligation to proceed",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-brand-accent" />
                    <span className="text-text-primary">{item}</span>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            <ContactForm defaultType="b2c" />
          </div>
        </div>
      </section>
    </div>
  );
}
